import React, { useEffect, useState } from 'react';
import { Plus, X, MessageCircle, Clipboard, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatDate } from '../lib/format';
import { waLink, mergeTemplate } from '../lib/whatsapp';
import { TEMPLATES } from '../lib/content';
import { parseChat } from '../lib/parseChat';
import { toCSV, downloadCSV, parseCSV } from '../lib/csv';

const STATUSES = ['new', 'contacted', 'quoted', 'follow_up', 'booked', 'lost'];
const SOURCES = ['instagram', 'whatsapp', 'website', 'referral', 'planner', 'photographer', 'google'];
const EVENT_TYPES = ['bridal', 'party', 'photoshoot', 'editorial'];

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [availability, setAvailability] = useState({});

  const load = async () => {
    const r = await api.get('/enquiries');
    setItems(r.data);
    const a = await api.get('/availability');
    setAvailability(a.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter(e => {
    if (statusFilter === 'open') return !['booked','lost'].includes(e.status);
    if (statusFilter === 'all') return true;
    return e.status === statusFilter;
  });

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/enquiries/${data.id}`, data);
      else await api.post('/enquiries', data);
      toast.success('Enquiry saved');
      setOpen(false); setEdit(null); load();
    } catch { toast.error('Save failed'); }
  };

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div><div className="eyebrow">Pipeline</div><h1 className="serif text-[28px]">Enquiry Inbox</h1></div>
        <button className="btn-gold" onClick={() => { setEdit({ status: 'new' }); setOpen(true); }} data-testid="enquiry-add-btn">
          <Plus size={16}/> New Enquiry
        </button>
      </div>

      <div className="mb-3 flex gap-2 flex-wrap">
        <button className="btn-ghost text-xs" onClick={async () => {
          try {
            const t = await navigator.clipboard.readText();
            if (!t) return toast.error('Clipboard empty');
            const p = parseChat(t);
            setEdit({ ...p, notes: t, status: 'new', source: 'whatsapp' });
            setOpen(true);
            toast.success('Parsed — review fields');
          } catch { toast.error('Clipboard access denied — paste inside the form'); }
        }} data-testid="enquiry-paste"><Clipboard size={12}/> Paste chat</button>
        <button className="btn-ghost text-xs" onClick={() => {
          const cols = [
            { key: 'name', label: 'Name' }, { key: 'phone', label: 'Phone' },
            { key: 'event_date', label: 'Event Date' }, { key: 'event_type', label: 'Event Type' },
            { key: 'source', label: 'Source' }, { key: 'status', label: 'Status' },
            { key: 'next_follow_up', label: 'Next Follow-up' }, { key: 'notes', label: 'Notes' },
            { key: 'created_at', label: 'Created' },
          ];
          downloadCSV(`enquiries-${new Date().toISOString().slice(0,10)}.csv`, toCSV(items, cols));
          toast.success(`Exported ${items.length} enquiries`);
        }} data-testid="enquiry-export"><Download size={12}/> Export CSV</button>
        <label className="btn-ghost text-xs cursor-pointer" data-testid="enquiry-import-label">
          <Upload size={12}/> Import
          <input type="file" accept=".csv,text/csv" hidden onChange={async (e) => {
            const file = e.target.files?.[0]; if (!file) return;
            const text = await file.text();
            const parsed = parseCSV(text);
            let ok = 0;
            for (const r of parsed.rows) {
              try { await api.post('/enquiries', { name: r['Name']||'', phone: r['Phone']||'', event_date: r['Event Date']||'', event_type: r['Event Type']||'', source: r['Source']||'website', status: r['Status']||'new', notes: r['Notes']||'' }); ok++; } catch {}
            }
            toast.success(`Imported ${ok} enquiries`);
            load();
            e.target.value = '';
          }}/>
        </label>
      </div>

      {/* Workflow hint */}
      <div className="card-alt p-3 mb-4 text-xs text-[var(--muted)]" data-testid="enq-workflow-hint">
        <b>Workflow</b>: Enquiry → <a href="/quotations/new" className="text-[var(--gold)] underline">Quotation</a> → Convert-to-booking generates Project + Invoice · <a href="/quotations" className="text-[var(--gold)] underline">All quotations</a>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {['open','all',...STATUSES].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className="px-3 py-1.5 rounded-full text-xs tracking-[0.14em] uppercase whitespace-nowrap"
            style={{background: statusFilter===s?'var(--charcoal)':'transparent', color: statusFilter===s?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}
            data-testid={`enquiry-filter-${s}`}>
            {s.replace('_',' ')}
          </button>
        ))}
      </div>

      {/* Handle PWA share_target */}
      {new URLSearchParams(window.location.search).get('share') === '1' && (
        <ShareTargetBar onOpen={(txt) => { const p = parseChat(txt); setEdit({...p, notes: txt, status: 'new', source: 'whatsapp'}); setOpen(true); }}/>
      )}

      <div className="space-y-2">
        {filtered.length === 0 && <div className="card-cream p-8 text-center text-sm text-[var(--muted)] italic">No enquiries.</div>}
        {filtered.map(e => (
          <div key={e.id} className="card-cream p-4 cursor-pointer hover:bg-[var(--cream-alt)]" onClick={() => { setEdit(e); setOpen(true); }} data-testid={`enquiry-${e.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="serif text-[18px]">{e.name}</div>
                  <span className={`status-pill status-${e.status==='booked'?'ok':e.status==='lost'?'overdue':e.status==='new'?'pending':'draft'}`}>{e.status.replace('_',' ')}</span>
                </div>
                <div className="text-xs text-[var(--muted)] mt-1 flex gap-3 flex-wrap">
                  {e.event_type && <span className="uppercase tracking-[0.14em]">{e.event_type}</span>}
                  {e.event_date && <span>{formatDate(e.event_date)}</span>}
                  {e.source && <span>via {e.source}</span>}
                  {e.phone && <span>{e.phone}</span>}
                </div>
              </div>
              {e.phone && (
                <a href={waLink(e.phone, mergeTemplate(TEMPLATES.find(t=>t.key==='bridal_detail_form_prepayment').body, { client_name: e.name }))}
                   target="_blank" rel="noreferrer" onClick={ev => ev.stopPropagation()}
                   className="text-[var(--gold)]" data-testid={`enquiry-wa-${e.id}`}>
                  <MessageCircle size={18}/>
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && <EnquiryForm value={edit} availability={availability} onClose={() => { setOpen(false); setEdit(null); }} onSave={save} />}
    </div>
  );
}

function ShareTargetBar({ onOpen }) {
  const p = new URLSearchParams(window.location.search);
  const text = p.get('text') || p.get('title') || '';
  useEffect(() => {
    if (text) onOpen(text);
    // eslint-disable-next-line
  }, []);
  return null;
}

function EnquiryForm({ value, availability, onClose, onSave }) {
  const [f, setF] = useState({ name: '', phone: '', event_date: '', event_type: 'bridal', source: 'instagram', notes: '', status: 'new', next_follow_up: '', ...value });
  const clash = f.event_date && availability[f.event_date];
  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
      <div className="w-full max-w-lg card-cream p-6 lg:rounded-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="serif text-[22px]">{value?.id ? 'Edit Enquiry' : 'New Enquiry'}</h2>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <div><label className="eyebrow">Name</label><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="enquiry-form-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Phone</label><input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} placeholder="+91 …" data-testid="enquiry-form-phone"/></div>
            <div><label className="eyebrow">Event Date</label><input type="date" value={f.event_date} onChange={e => setF({ ...f, event_date: e.target.value })} data-testid="enquiry-form-date"/></div>
          </div>
          {clash && (
            <div className="rounded p-2 text-xs" style={{background:'rgba(180,100,90,0.12)', color:'var(--overdue)'}}>
              ⚠ Date clash — {clash.state} for {clash.project_name} ({clash.event_name})
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Event Type</label>
              <select value={f.event_type} onChange={e => setF({ ...f, event_type: e.target.value })} data-testid="enquiry-form-type">
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="eyebrow">Source</label>
              <select value={f.source} onChange={e => setF({ ...f, source: e.target.value })} data-testid="enquiry-form-source">
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Status</label>
              <select value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="enquiry-form-status">
                {STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
              </select>
            </div>
            <div><label className="eyebrow">Next Follow-up</label><input type="date" value={f.next_follow_up || ''} onChange={e => setF({ ...f, next_follow_up: e.target.value })} data-testid="enquiry-form-followup"/></div>
          </div>
          {f.status === 'lost' && <div><label className="eyebrow">Lost reason</label><input value={f.lost_reason || ''} onChange={e => setF({ ...f, lost_reason: e.target.value })} /></div>}
          <div><label className="eyebrow">Notes</label><textarea rows={3} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="enquiry-form-notes" /></div>
          <button className="btn-gold w-full" onClick={() => onSave(f)} data-testid="enquiry-form-save">Save Enquiry</button>
        </div>
      </div>
    </div>
  );
}
